/**
 * `#/display` — the LIVE shared-display board (#582 slice 5, design C1).
 *
 * The kitchen screen shows every member's lanterns, from the SERVER's device board
 * (`GET /api/dashboard-data`, device projection). Nobody is signed in on it. To light a lantern a
 * family member — adult or child (D-1) — holds their own lantern for 600 ms and enters their own PIN.
 * The server then holds a short-lived ACTOR for that person: 30 seconds from the last deliberate
 * interaction (D-2), never renewed by a read, a timer or a socket event.
 *
 * What this screen never does, each for a measured reason in the design:
 *
 *  - It never shows a sample family, or success the server did not confirm. A pre-v2 display
 *    (still on a borrowed-admin session until slice 6) sees "not switched on yet", not a board.
 *  - It never sends an interaction on its own. `lib/actorInteraction.js` is called from pointer and
 *    click handlers only; the countdown timers here only redraw.
 *  - It never keeps an actor across sleep (OQ-2): hiding the page ends it and deactivates, and on
 *    wake every lantern stays inert until deactivate AND verify have both answered.
 *  - Undo names the execution id the completion returned, inside the server's 30-second window
 *    measured from the server's completion time (OQ-4). A late reply does not buy a fresh window.
 *
 * Kept apart on screen, because they are different facts: the hold (the lantern fills), the
 * request in flight ("One moment…"), the saved completion ("Completion saved"), a completion a
 * parent still has to approve ("Awaiting approval"), and money — which this board never shows.
 *
 * Registered in `App.jsx` as a live container for the existing `/display` route.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client.js';
import { createBoardSocket } from '../api/boardSocket.js';
import { claimAsActor, completeAsActor, fetchDeviceBoard, fetchDeviceProfiles, undoAsActor } from '../api/displays.js';
import {
  activateDeviceActor,
  deactivateDeviceActor,
  fetchDeviceActor,
  fetchDisplaySession,
  isActorEnded,
  reportDeviceActorInteraction,
} from '../api/session.js';
import { LANTERN_MARK } from '../assets.js';
import ChoreMoveControl from '../components/ChoreMoveControl.jsx';
import LanternLoader from '../components/LanternLoader.jsx';
import { KioskChime } from '../components/FeedbackControls.jsx';
import { createInteractionReporter } from '../lib/actorInteraction.js';
import { SHARED, SHARED_TITLE, moveTargets } from '../lib/choreMoves.js';
import { AUDIO_ASSETS } from '../lib/audioAssets.js';
import { toBoardKids, toKioskSound, toReviewedCompletions, toSharedQuests, toUndoneCompletions } from '../lib/deviceBoardMapping.js';
import { FeedbackProvider } from '../state/FeedbackPreferences.jsx';

// Lazy, like every other live container's page (`TodayRoute`, `SettingsRoute`): the board page and
// the lantern gesture are large, and the shell must not load them for a family that never opens
// `#/display`.
const Display = lazy(() => import('../pages/Display.jsx'));
const LanternQuest = lazy(() => import('../gestures/lanternRitual.jsx').then((m) => ({ default: m.LanternQuest })));

export const DISPLAY_PATH = '/display';
/** The server's window (`server/services/chore.service.js` `DISPLAY_ACTOR_UNDO_WINDOW_MS`), 30 s. */
export const UNDO_WINDOW_MS = 30_000;
/** The one event the device room carries (`server/realtime/tenantRooms.js`). */
export const BOARD_SIGNAL = 'board.changed';

/** A failed board read is tried again after these waits, then every 30 s, until one succeeds. */
const BOARD_RETRY_MS = [2_000, 4_000, 8_000, 15_000, 30_000];
const TURN_ENDED = 'Your turn ended. Hold your lantern and enter your code again.';
const PIN_REJECTED = 'That code did not work — try again.';
const PIN_FAULT = 'We could not check that code just now — please try again in a moment.';
const PIN_SHAPE = /^(\d{4}|\d{6})$/;
const UNDO_UNKNOWN = 'We couldn’t confirm the undo. We don’t know whether it reversed — check again in a moment.';
/**
 * A definite note on a receipt (a parent reviewed it, it was already undone, it is too late) outlives
 * board reads for this long after it appears, then the board is authoritative again. 10 s: long enough
 * to read, and bounded so it cannot sit over a quest that moved on. It is a reading time, not the
 * undo window, and deliberately did not follow that window to 30 s (d-20260928-48).
 */
const NOTE_HOLD_MS = 10_000;
// Eligibility is the saved completion's deadline; a stale actor read cannot shorten it.
const undoWindowOpen = (record, now) => Number.isFinite(record?.deadline) && record.deadline > now;
const UNDONE_ELSEWHERE = 'This was undone.';
// `approvals` (a child asked for approval) has no echo: nobody reviewed anything yet. Only
// `reviews`, a parent's approval (`tenantRooms.js` DEVICE_SIGNAL_REASON), says a parent reviewed.
const ECHO = Object.assign(Object.create(null), {
  reviews: 'A parent just reviewed some lanterns.',
  payday: 'Payday was just settled. The ChoreStory app has the details.',
});
/**
 * The device reasons that deliberately say nothing: the board re-read is the whole message. With
 * `ECHO` this accounts for every reason `tenantRooms.js` sends, its `household` fallback included;
 * `app/test/event-compatibility.test.js` holds both lists to that register. SILENT_REASONS is read
 * by that source test only; the runtime uses ECHO and ignores reasons without an echo.
 */
export const SILENT_REASONS = Object.freeze(['chores', 'approvals', 'household']);

function newKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function clockLabel(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function Notice({ title, children }) {
  return (
    <main style={{ padding: '2rem', maxWidth: '36rem' }} data-component="DisplayRoute">
      <h1>{title}</h1>
      {children}
    </main>
  );
}

export default function DisplayRoute({ socketFactory = createBoardSocket }) {
  const [phase, setPhase] = useState('checking');
  const [board, setBoard] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [stale, setStale] = useState(false);
  const [live, setLive] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [actor, setActor] = useState(null);
  const [pin, setPin] = useState(null);
  const [receipts, setReceipts] = useState({});
  const receiptsRef = useRef(receipts);
  receiptsRef.current = receipts;
  const completionNotice = useRef(null);
  const [busy, setBusy] = useState({});
  // #583: a chore being dragged (its allowed drop keys, and the one under the finger), and the
  // moves in flight. A move is never drawn as done here; the next board read shows where it went.
  const [drag, setDrag] = useState(null);
  const [moving, setMoving] = useState({});
  const movingRef = useRef(new Set());
  const [notice, setNotice] = useState('');
  const [waking, setWaking] = useState(false);
  const [, setTick] = useState(0);
  const [gestureVersion, setGestureVersion] = useState(0);
  // #577: which household this display belongs to, so its sound follows THAT family's board.
  const [deviceIdentity, setDeviceIdentity] = useState(null);

  const alive = useRef(true);
  const actorRef = useRef(null);
  const skewRef = useRef(0);
  const intentRef = useRef(null);
  const attemptKeys = useRef(new Map());
  const loading = useRef(null);
  const asleep = useRef(false);
  const boardRef = useRef(null);
  const socketRef = useRef(null);
  const retry = useRef({ timer: null, attempt: 0 });
  const loadBoardRef = useRef(null);
  const epoch = useRef(0);
  const verified = useRef(false);
  const access = useRef({ live: false, stale: true, recovering: false });
  const activation = useRef(null);
  const executing = useRef(new Set());
  const undoing = useRef(new Set());
  const recoverRef = useRef(null);

  function canAct() {
    return alive.current && verified.current && !asleep.current
      && access.current.live && !access.current.stale && !access.current.recovering;
  }

  function cancelIntent() {
    epoch.current += 1;
    intentRef.current = null;
    setPin(null);
    setGestureVersion((n) => n + 1);
  }


  function stopBoardRetry() {
    clearTimeout(retry.current.timer);
    retry.current = { timer: null, attempt: 0 };
  }

  /**
   * A wake or reconnect step that failed on the network tries the WHOLE barrier again, on the
   * board's backoff. Without it one lost deactivation or verify left the board read-only until the
   * page happened to hide and show again, because a connected socket sends no reconnect.
   */
  function scheduleRecovery() {
    if (asleep.current || !alive.current) return;
    const wait = BOARD_RETRY_MS[Math.min(retry.current.attempt, BOARD_RETRY_MS.length - 1)];
    clearTimeout(retry.current.timer);
    retry.current = {
      attempt: retry.current.attempt + 1,
      timer: setTimeout(() => {
        retry.current.timer = null;
        if (access.current.recovering) recoverRef.current?.();
      }, wait),
    };
  }

  const serverNow = useCallback(() => Date.now() + skewRef.current, []);

  const reporter = useMemo(() => createInteractionReporter({
    send: (body) => {
      reportDeviceActorInteraction(body).then((result) => {
        if (!alive.current || actorRef.current?.generation !== body.generation) return;
        if (result.outcome === 'ended') endActorLocally();
        else adoptActor(result.actor);
      }).catch(() => {
        // Best effort. The server's deadline stands either way.
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  /**
   * Take the server's actor, and the server's clock with it. `canMoveAnyChore` comes only from the
   * activation answer (#583); a later read of the same generation keeps it, a new one starts false.
   */
  function adoptActor(value, { canMoveAnyChore } = {}) {
    if (!value) return;
    skewRef.current = Date.parse(value.serverNow) - Date.now();
    const previous = actorRef.current;
    const next = {
      userId: value.userId,
      firstName: value.firstName ?? null,
      generation: value.generation,
      expiresAtMs: Date.parse(value.expiresAt),
      canMoveAnyChore: canMoveAnyChore ?? (previous?.generation === value.generation && previous.canMoveAnyChore === true),
    };
    actorRef.current = next;
    setActor(next);
  }

  function endActorLocally() {
    reporter.clear();
    setDrag(null);
    setGestureVersion((n) => n + 1);
    actorRef.current = null;
    setActor(null);
  }

  /** End the actor here and on the server. The server's own deadline is the backstop. */
  function clearActor({ keepalive = false } = {}) {
    const had = actorRef.current !== null;
    endActorLocally();
    return deactivateDeviceActor({ keepalive }).catch(() => {
      if (had) setNotice('This display could not reach the server; the last code expires on its own within 30 seconds.');
    });
  }

  const refused = useCallback(() => {
    verified.current = false;
    cancelIntent();
    attemptKeys.current.clear();
    endActorLocally();
    stopBoardRetry();
    // A refusal is an answer, not a transport fault: the live socket must not keep reconnecting.
    socketRef.current?.close();
    socketRef.current = null;
    intentRef.current = null;
    setPin(null);
    setBoard(null);
    boardRef.current = null;
    setProfiles([]);
    setReceipts({});
    setPhase('none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Read the board. Concurrent calls share one request, then one follow-up. */
  const loadBoard = useCallback(async () => {
    if (loading.current && loading.current.epoch !== epoch.current) {
      const started = epoch.current;
      await loading.current.promise;
      if (started !== epoch.current || !alive.current || asleep.current) return false;
      return loadBoardRef.current();
    }
    if (loading.current) {
      loading.current.again = true;
      return loading.current.promise;
    }
    const run = { again: false, epoch: epoch.current };
    loading.current = run;
    run.promise = (async () => {
      try {
        do {
          run.again = false;
          try {
            const uncertain = [...attemptKeys.current].filter(([, attempt]) => attempt.uncertain);
            const [nextBoard, nextProfiles] = await Promise.all([fetchDeviceBoard(), fetchDeviceProfiles()]);
            if (!alive.current || run.epoch !== epoch.current || !verified.current || asleep.current) return false;
            stopBoardRetry();
            // A successful authoritative read ends uncertain attempts that preceded this read.
            for (const [id, attempt] of uncertain) {
              if (!executing.current.has(id) && attemptKeys.current.get(id) === attempt) attemptKeys.current.delete(id);
            }
            const statuses = new Map(toBoardKids(nextBoard).flatMap((kid) => kid.quests.map((q) => [String(q.id), q.status])));
            const reviewed = toReviewedCompletions(nextBoard);
            const undone = toUndoneCompletions(nextBoard);
            const announced = completionNotice.current;
            if (announced && ((announced.status === 'pending' && reviewed.has(String(announced.executionId)))
                || undone.has(String(announced.executionId)))) {
              // A completion announcement is persistent too; do not leave it contradicting the receipt.
              setNotice((previous) => previous === announced.message ? '' : previous);
            }
            const at = serverNow();
            setReceipts((previous) => Object.fromEntries(Object.entries(previous).flatMap(([id, record]) => {
              // Keep terminal facts received during Undo without changing its visible in-flight
              // state. A later failed recovery read cannot erase what this board already proved.
              if (record.busy) {
                const boardStatus = undone.has(String(record.executionId)) ? 'undone'
                  : record.status === 'pending' && reviewed.has(String(record.executionId)) ? 'reviewed' : record.boardStatus;
                return [[id, boardStatus ? { ...record, boardStatus } : record]];
              }
              // The server says THIS completion was undone (a parent in the app, say). Named, not
              // inferred from absence, so a read that began before it committed cannot end it.
              if ((record.status === 'pending' || record.status === 'done') && !record.busy
                  && undone.has(String(record.executionId))) {
                return [[id, { ...record, status: 'undone', deadline: null, note: UNDONE_ELSEWHERE, noteUntil: at + NOTE_HOLD_MS }]];
              }
              // A parent decided THIS completion (matched by its id): the undo window ends and the
              // receipt stops saying "Awaiting approval".
              if (record.status === 'pending' && !record.busy && reviewed.has(String(record.executionId))) {
                return [[id, { ...record, status: 'reviewed', deadline: null, note: 'A parent reviewed this.', noteUntil: at + NOTE_HOLD_MS }]];
              }
              // Preserve the active undo ritual or request, and a definite note for NOTE_HOLD_MS;
              // afterward the board is authoritative.
              return record.busy || record.deadline > at || record.noteUntil > at || !statuses.has(id) ? [[id, record]] : [];
            })));
            access.current.stale = false;
            setPhase((value) => value === 'error' ? 'board' : value);
            boardRef.current = nextBoard;
            setBoard(nextBoard);
            setProfiles(nextProfiles);
            setStale(false);
            setLastSync(Date.now());
          } catch (error) {
            if (!alive.current || run.epoch !== epoch.current) return false;
            if (error instanceof ApiError && error.status === 401) {
              refused();
              return;
            }
            // A transport fault keeps the last board, visibly stale and read-only, and tries again
            // on its own: otherwise one failed read on a quiet evening leaves every lantern inert
            // until somebody elsewhere happens to change something. A refusal the server meant
            // (another 4xx) is not retried; it stays visibly stale.
            access.current.stale = true;
            setGestureVersion((n) => n + 1);
            setStale(true);
            if (!boardRef.current) setPhase('error');
            const transient = !(error instanceof ApiError)
              || error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
            if (transient && !asleep.current) {
              const wait = BOARD_RETRY_MS[Math.min(retry.current.attempt, BOARD_RETRY_MS.length - 1)];
              clearTimeout(retry.current.timer);
              retry.current = {
                attempt: retry.current.attempt + 1,
                timer: setTimeout(() => {
                  retry.current.timer = null;
                  // A wake/reconnect read must finish its barrier as well as refresh the snapshot.
                  if (access.current.recovering) recoverRef.current?.();
                  else loadBoardRef.current?.();
                }, wait),
              };
            }
          }
        } while (run.again && run.epoch === epoch.current);
        return run.epoch === epoch.current && !access.current.stale;
      } finally {
        // Cleared as the run ends, not a microtask later: a caller arriving after the last check of
        // `again` must start a fresh read rather than await this finished one.
        if (loading.current === run) loading.current = null;
      }
    })();
    return run.promise;
  }, [refused, serverNow]);
  loadBoardRef.current = loadBoard;

  /** Ask the server what this browser is. Returns true when it is a live v2 display. */
  const verify = useCallback(async () => {
    const started = epoch.current;
    let answer;
    try {
      answer = await fetchDisplaySession();
    } catch {
      if (!alive.current || started !== epoch.current) return false;
      setStale(true);
      if (!boardRef.current) setPhase('error');
      if (access.current.recovering) scheduleRecovery();
      return false;
    }
    if (!alive.current || started !== epoch.current || asleep.current) return false;
    if (answer.status === 'none') {
      refused();
      return false;
    }
    if (answer.status === 'person') {
      verified.current = false;
      setPhase('person');
      return false;
    }
    if (answer.device.contractVersion !== 2) {
      verified.current = false;
      setPhase('not-ready');
      return false;
    }
    verified.current = true;
    setDeviceIdentity((prev) => (prev && prev.tenantId === answer.device.tenantId
      && prev.displayTokenId === answer.device.displayTokenId
      ? prev
      : { tenantId: answer.device.tenantId, displayTokenId: answer.device.displayTokenId }));
    setPhase('board');
    return true;
  }, [refused]);

  // Recovery is a barrier: no delayed activation or control crosses it.
  async function recover() {
    cancelIntent();
    endActorLocally();
    access.current.recovering = true;
    setWaking(true);
    const started = epoch.current;
    try {
      // An activation can commit after the page hid. Clear after it settles, not before.
      await activation.current?.promise.catch(() => {});
      if (started !== epoch.current || asleep.current || !alive.current) return;
      await deactivateDeviceActor();
      if (started !== epoch.current || asleep.current || !alive.current) return;
      if (!(await verify())) return;
      if (!(await loadBoard())) return;
      if (started !== epoch.current || asleep.current || !alive.current) return;
      access.current.recovering = false;
      setWaking(false);
    } catch (error) {
      if (started !== epoch.current || !alive.current) return;
      if (error instanceof ApiError && error.status === 401) refused();
      else {
        access.current.stale = true;
        setStale(true);
        setNotice('The board is read-only while it reconnects.');
        scheduleRecovery();
      }
    }
  }
  recoverRef.current = recover;

  // ---- boot, and the live signal
  useEffect(() => {
    alive.current = true;
    // Per run, not the shared `alive` ref: under StrictMode the second mount sets `alive` back to
    // true before the first run's awaits resume, and that run must not open a socket of its own.
    let cancelled = false;
    let socket = null;
    let joinedBefore = false;
    let refreshTimer = null;
    const keys = attemptKeys.current;
    (async () => {
      if (!(await verify()) || cancelled) return;
      await loadBoard();
      if (cancelled || !alive.current || !verified.current) return;
      socket = socketFactory();
      socketRef.current = socket;
      socket.on('connect', () => {
        access.current.live = true;
        setLive(true);
        if (!joinedBefore) {
          joinedBefore = true;
          loadBoard();
          return;
        }
        // A reconnect starts over (design S5): no actor survives it, and the board is re-read
        // rather than replayed.
        if (!asleep.current) recoverRef.current();
      });
      socket.on('disconnect', () => {
        access.current.live = false;
        setLive(false);
        cancelIntent();
        endActorLocally();
      });
      socket.on('connect_error', () => {
        access.current.live = false;
        cancelIntent();
        endActorLocally();
        setLive(false);
        verify();
      });
      socket.on('board.changed', (payload) => {
        const echo = ECHO[payload?.reason];
        if (echo) setNotice(echo);
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => loadBoard(), 150);
      });
    })();
    return () => {
      cancelled = true;
      alive.current = false;
      epoch.current += 1;
      keys.clear();
      clearTimeout(refreshTimer);
      stopBoardRetry();
      reporter.clear();
      socket?.close();
      if (socketRef.current === socket) socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- the actor's server deadline: redraw each second, and return to the board when it passes
  // A held note counts too, so the screen redraws when it expires rather than at the next board read.
  const hasCountdown = actor !== null || Object.values(receipts).some((r) => r.deadline || r.noteUntil > serverNow());
  useEffect(() => {
    if (!hasCountdown) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [hasCountdown]);
  useEffect(() => {
    if (!actor) return undefined;
    const timer = setTimeout(() => {
      if (actorRef.current?.generation === actor.generation) endActorLocally();
    }, Math.max(0, actor.expiresAtMs - serverNow()));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, serverNow]);

  // ---- sleep and wake (OQ-2)
  useEffect(() => {
    function sleep() {
      if (asleep.current) return;
      asleep.current = true;
      cancelIntent();
      attemptKeys.current.clear();
      access.current.recovering = true;
      setWaking(true);
      // Wake re-reads the board after verify; a retry must not light the board up before that.
      stopBoardRetry();
      intentRef.current = null;
      setPin(null);
      setReceipts({});
      clearActor({ keepalive: true });
    }
    async function wake() {
      if (!asleep.current) return;
      asleep.current = false;
      await recoverRef.current();
    }
    const onVisibility = () => (document.hidden ? sleep() : wake());
    const onPageShow = () => { if (!document.hidden) wake(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', sleep);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', sleep);
      window.removeEventListener('pageshow', onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const controlsEnabled = phase === 'board' && live && !waking && !stale;

  function ownerOf(kid) {
    return profiles.find((p) => String(p.id) === String(kid.id)) ?? { id: kid.id, name: kid.name, pinSet: null };
  }

  function liveActorFor(ownerId) {
    const current = actorRef.current;
    if (!current || String(current.userId) !== String(ownerId)) return null;
    return current.expiresAtMs > serverNow() ? current : null;
  }

  async function refreshActorDeadline(generation) {
    try {
      const current = await fetchDeviceActor();
      if (!alive.current || actorRef.current?.generation !== generation) return;
      if (current && current.generation === generation) adoptActor(current);
      else endActorLocally();
    } catch {
      // The countdown keeps the last deadline the server gave; the server enforces the real one.
    }
  }

  async function execute(quest, current) {
    if (!canAct() || executing.current.has(quest.id)) return;
    executing.current.add(quest.id);
    const started = epoch.current;
    let attempt = attemptKeys.current.get(quest.id);
    if (!attempt || attempt.generation !== current.generation) {
      attempt = { operationKey: `display:${newKey()}`, generation: current.generation, uncertain: false };
      attemptKeys.current.set(quest.id, attempt);
    }
    const { operationKey } = attempt;
    setBusy((b) => ({ ...b, [quest.id]: true }));
    setNotice(`Sending — ${quest.title}.`);
    try {
      const receipt = await completeAsActor(quest.id, { generation: current.generation, operationKey });
      if (!alive.current || started !== epoch.current) return;
      if (receipt?.execution_id == null || typeof receipt.approval_pending !== 'boolean') {
        throw new Error('no execution receipt');
      }
      attemptKeys.current.delete(quest.id);
      const completedAt = Date.parse(receipt.completed_at ?? receipt.tracked_time);
      const record = {
        executionId: receipt.execution_id,
        status: receipt.approval_pending ? 'pending' : 'done',
        deadline: Number.isFinite(completedAt) ? completedAt + UNDO_WINDOW_MS : null,
        ownerId: current.userId,
        title: quest.title,
      };
      setReceipts((r) => ({ ...r, [quest.id]: record }));
      const message = `${record.status === 'pending' ? 'Awaiting approval' : 'Completion saved'} — ${quest.title}.`;
      completionNotice.current = { executionId: record.executionId, status: record.status, message };
      setNotice(message);
      refreshActorDeadline(current.generation);
      loadBoard();
    } catch (error) {
      if (!alive.current || started !== epoch.current) return;
      if (isActorEnded(error)) {
        attemptKeys.current.delete(quest.id);
        endActorLocally();
        setNotice(TURN_ENDED);
      } else if (error instanceof ApiError && error.status === 401) {
        refused();
      } else if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        // The server judged it and refused it: nothing was written.
        attemptKeys.current.delete(quest.id);
        setNotice(error.body?.code === 'OPERATION_KEY_REQUIRED'
          ? 'Please refresh the page and try again.'
          : `That lantern could not be lit here — ${quest.title}.`);
        loadBoard();
      } else {
        attempt.uncertain = true;
        // Nobody knows. The key is kept until a fresh authoritative board reconciles this attempt.
        setNotice(`We couldn’t confirm ${quest.title}. We don’t know whether it saved — check again in a moment.`);
        loadBoard();
      }
    } finally {
      executing.current.delete(quest.id);
      if (alive.current) setBusy((b) => ({ ...b, [quest.id]: false }));
    }
  }

  async function undo(quest) {
    const record = receiptsRef.current[quest.id];
    if (record?.executionId !== receipts[quest.id]?.executionId) return;
    const current = record ? liveActorFor(record.ownerId) : null;
    if (!canAct() || !record || !current || record.busy || undoing.current.has(record.executionId)) return;
    // A handler retained from an older render cannot reopen a terminal receipt or a closed window.
    if (!undoWindowOpen(record, serverNow()) || (record.status !== 'pending' && record.status !== 'done')) return;
    undoing.current.add(record.executionId);
    const started = epoch.current;
    // Onto the CURRENT record: a board read may have reviewed or ended it since this render.
    setReceipts((r) => {
      const cur = r[quest.id];
      if (!cur || cur.executionId !== record.executionId || (cur.status !== 'pending' && cur.status !== 'done')) return r;
      return { ...r, [quest.id]: { ...cur, busy: true, note: '' } };
    });
    try {
      await undoAsActor(record.executionId, { generation: current.generation });
      if (!alive.current || started !== epoch.current) return;
      setReceipts((r) => {
        const next = { ...r };
        delete next[quest.id];
        return next;
      });
      setNotice(`Undone — ${quest.title}.`);
      refreshActorDeadline(current.generation);
      loadBoard();
    } catch (error) {
      if (!alive.current || started !== epoch.current) return;
      if (error instanceof ApiError && error.status === 401) { refused(); return; }
      const paydayBusy = error instanceof ApiError && error.status === 503 && error.body?.code === 'PAYDAY_BUSY';
      const streakBusy = error instanceof ApiError && error.status === 503 && error.body?.code === 'STREAK_BUSY';
      const retryableBusy = paydayBusy || streakBusy;
      let note = UNDO_UNKNOWN;
      let status;
      if (isActorEnded(error)) {
        endActorLocally();
        note = 'Your turn ended, so this cannot be undone here.';
      } else if (retryableBusy) {
        note = streakBusy ? 'Streak history was updating, so nothing was undone. Try again.'
          : 'Another payday change was in progress, so nothing was undone. Try again.';
      } else if (error instanceof ApiError && error.body?.code === 'UNDO_WINDOW_CLOSED') {
        note = 'It is too late to undo that here.';
      } else if (error instanceof ApiError && error.body?.code === 'ALREADY_REVIEWED') {
        // A definite answer: the completion stands and a parent decided it, so it no longer awaits one.
        note = 'A parent already reviewed this, so it can’t be undone here.';
        status = 'reviewed';
      } else if (error instanceof ApiError && error.body?.code === 'ALREADY_IN_PAYDAY') {
        // #580, definite too: a no-review reward a payout or a payday hold already names. The completion
        // stands and nobody reviewed it, so the note never says a parent did. 'reviewed' is the display's
        // "final, no Undo" status (lanternRitual.jsx) and renders "Completion saved".
        note = 'This reward is already in payday, so it can’t be undone here.';
        status = 'reviewed';
      } else if (error instanceof ApiError && error.body?.code === 'ALREADY_UNDONE') {
        // Definite too: the completion is already reversed, so the lantern follows the board.
        note = 'This was already undone.';
        status = 'undone';
      }
      const announced = completionNotice.current;
      if (status && announced?.executionId === record.executionId) {
        // This refusal is already authoritative even when the recovery board cannot be reached.
        setNotice((previous) => previous === announced.message ? '' : previous);
      }
      // Only a definite answer is held against the next board reads; "we don't know" yields to them.
      const noteUntil = retryableBusy ? record.deadline
        : note === UNDO_UNKNOWN ? undefined : serverNow() + NOTE_HOLD_MS;
      // Write onto the CURRENT record, and only while it is still this completion's: a second undo
      // failing after the first succeeded must not bring back a receipt the server already reversed.
      setReceipts((r) => {
        const cur = r[quest.id];
        if (!cur || cur.executionId !== record.executionId) return r;
        const boardStatus = cur.boardStatus;
        const settledNote = boardStatus === 'undone' ? UNDONE_ELSEWHERE
          : boardStatus === 'reviewed' ? 'A parent reviewed this.' : note;
        return { ...r, [quest.id]: {
          ...cur, busy: false, deadline: retryableBusy && !boardStatus ? cur.deadline : null, note: settledNote,
          noteUntil: boardStatus ? serverNow() + NOTE_HOLD_MS : noteUntil,
          status: boardStatus ?? status ?? cur.status,
        } };
      });
      // A board fact may have arrived while this receipt was busy. Re-read after settling the
      // request, including inconclusive and late/actor refusals; no second socket event is needed.
      loadBoard();
    } finally {
      undoing.current.delete(record.executionId);
    }
  }

  /**
   * #583 d-20261001-42 — move a chore as the signed-in actor. The server decides; the chore stays
   * where it is drawn until the next board read says it moved.
   */
  async function moveChore(quest, target) {
    const current = actorRef.current ? liveActorFor(actorRef.current.userId) : null;
    if (!canAct() || !current || movingRef.current.has(quest.id)) return;
    movingRef.current.add(quest.id);
    const started = epoch.current;
    setMoving((m) => ({ ...m, [quest.id]: true }));
    setNotice(`Moving — ${quest.title}.`);
    try {
      await claimAsActor(quest.id, target.userId, { generation: current.generation });
      if (!alive.current || started !== epoch.current) return;
      const holder = target.userId === null ? null : kids.find((k) => String(k.id) === String(target.userId));
      setNotice(target.userId === null
        ? `Moved — ${quest.title} is back in “${SHARED_TITLE}”.`
        : `Moved — ${quest.title} is now ${holder?.name ?? 'theirs'}’s.`);
      // A saved move renewed the actor on the server, like a saved completion: read its deadline.
      refreshActorDeadline(current.generation);
      loadBoard();
    } catch (error) {
      if (!alive.current || started !== epoch.current) return;
      if (error instanceof ApiError && error.status === 401) {
        refused();
        return;
      }
      if (isActorEnded(error)) {
        endActorLocally();
        setNotice(`Your turn ended, so ${quest.title} stayed where it was. Hold your lantern and enter your code again.`);
      } else if (error instanceof ApiError && error.body?.code === 'ACTOR_NOT_PARENT') {
        setNotice(`Only a parent can move a chore to someone else, so ${quest.title} stayed where it was.`);
      } else if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setNotice(`${quest.title} could not be moved here, so it stayed where it was.`);
      } else {
        setNotice(`We couldn’t confirm moving ${quest.title}. Check the board in a moment.`);
      }
      loadBoard();
    } finally {
      movingRef.current.delete(quest.id);
      if (alive.current) setMoving((m) => ({ ...m, [quest.id]: false }));
    }
  }

  /** A lantern's hold finished (or its confirm button was pressed). */
  async function onIntent(quest, kid) {
    if (!canAct() || busy[quest.id] || activation.current) return;
    const started = epoch.current;
    const owner = ownerOf(kid);
    const current = liveActorFor(owner.id);
    if (current) {
      execute(quest, current);
      return;
    }
    // Somebody else was acting, or the actor ran out: it ends before anyone new is asked.
    if (actorRef.current) await clearActor();
    if (started !== epoch.current || !canAct()) return;
    if (owner.pinSet === false) {
      setNotice(`${owner.name ?? 'This person'} needs a code first. Ask a parent to set one in the ChoreStory app.`);
      return;
    }
    intentRef.current = { questId: quest.id, ownerId: owner.id };
    setPin({ owner, value: '', error: null, busy: false });
  }

  function cancelPin() {
    cancelIntent();
    endActorLocally();
  }

  async function submitPin(value) {
    const current = pin;
    if (!canAct() || !current || activation.current || !PIN_SHAPE.test(value)) return;
    const started = epoch.current;
    const pending = {};
    activation.current = pending;
    setPin({ ...current, busy: true, error: null });
    let result;
    try {
      pending.promise = activateDeviceActor(current.owner.id, value);
      result = await pending.promise;
    } catch (error) {
      if (!alive.current || started !== epoch.current) return;
      if (error instanceof ApiError && error.status === 401) {
        refused();
        return;
      }
      setPin({ ...current, value: '', busy: false, error: PIN_FAULT });
      return;
    } finally {
      if (activation.current === pending) activation.current = null;
    }
    if (!alive.current || started !== epoch.current || !canAct()) {
      // A canceled activation can have committed on the server after our earlier clear.
      deactivateDeviceActor({ keepalive: true }).catch(() => {});
      return;
    }
    if (result.outcome !== 'ok') {
      const error = result.outcome === 'locked'
        ? `Too many tries. Wait ${result.retryAfterSeconds ?? 'a few'} seconds, then try again.`
        : result.outcome === 'conflict'
          ? 'Someone else just chose a profile — try again.'
          : PIN_REJECTED;
      setPin({ ...current, value: '', busy: false, error });
      return;
    }
    adoptActor(result.actor, { canMoveAnyChore: result.canMoveAnyChore === true });
    setPin(null);
    const intent = intentRef.current;
    intentRef.current = null;
    if (!intent || String(intent.ownerId) !== String(result.actor.userId)) return;
    // Performed ONCE, after a fresh look at the board: the chore may have moved while the PIN was typed.
    const fresh = await loadBoard();
    if (!fresh || !canAct() || started !== epoch.current) return;
    // The actor may have ended during that read (its deadline, sleep, a refusal). Nothing was sent,
    // so say that plainly rather than letting a null actor surface as "we don't know if it saved".
    const acting = liveActorFor(intent.ownerId);
    if (!acting) {
      setNotice(TURN_ENDED);
      return;
    }
    const kid = toBoardKids(boardRef.current).find((k) => String(k.id) === String(intent.ownerId));
    const quest = kid?.quests.find((q) => q.id === intent.questId && q.status === 'unlit');
    if (!quest) {
      setNotice('That lantern has changed, so nothing was lit. Take another look at the board.');
      return;
    }
    execute(quest, acting);
  }

  // ---- render
  const kids = useMemo(() => toBoardKids(board), [board]);
  const sharedQuests = useMemo(() => toSharedQuests(board), [board]);
  // #577 (d-20260924-03): the board already carries only what this display may play.
  const kioskSound = useMemo(() => toKioskSound(board), [board]);
  const soundActor = useMemo(() => (deviceIdentity?.tenantId
    ? { kind: 'device', familyId: deviceIdentity.tenantId, id: String(deviceIdentity.displayTokenId ?? '') }
    : null), [deviceIdentity]);

  if (phase === 'checking') {
    return <Notice title="Waking the family board…"><p role="status">Checking this display with the server.</p></Notice>;
  }
  if (phase === 'none') {
    return (
      <Notice title="This display needs to be paired">
        <p>The server does not recognise this display any more. A parent may have unpaired it, or its sign-in ended.</p>
        <p><a href="#/pair">Pair this display</a> with a new code from Family Studio.</p>
      </Notice>
    );
  }
  if (phase === 'person') {
    return (
      <Notice title="This browser is signed in as a person">
        <p>The family board runs on a shared display, not on a personal sign-in.</p>
        <p><a href="#/pair">Pair this display</a> to use it as the family board.</p>
      </Notice>
    );
  }
  if (phase === 'not-ready') {
    return (
      <Notice title="This display is paired">
        <p role="status">
          The live board for shared displays is not switched on yet. It will appear here after the next
          server update. Nothing on this screen is pretend, so for now it stays empty.
        </p>
      </Notice>
    );
  }

  const now = serverNow();
  const actorLeft = actor ? Math.max(0, Math.ceil((actor.expiresAtMs - now) / 1000)) : 0;

  // #583: the move control, only while someone is signed in and only with the targets they may use.
  const mover = actor && controlsEnabled && liveActorFor(actor.userId) ? actor : null;
  const members = kids.map((k) => ({ id: k.id, name: k.name }));
  const renderMove = (quest, ownerId) => {
    const targets = mover ? moveTargets({ actor: mover, ownerId, members }) : [];
    if (!targets.length) return null;
    return (
      <ChoreMoveControl
        title={quest.title}
        targets={targets}
        busy={!!moving[quest.id]}
        onMove={(target) => moveChore(quest, target)}
        onInteract={() => reporter.report(mover.generation, 'activate-control')}
        onDragChange={({ active, overKey }) => setDrag(active ? { keys: targets.map((t) => t.key), overKey } : null)}
      />
    );
  };
  const columnProps = (memberId) => {
    const key = memberId === null ? SHARED : String(memberId);
    if (!drag || !drag.keys.includes(key)) return { 'data-drop-target': key };
    return { 'data-drop-target': key, 'data-drop-state': drag.overKey === key ? 'over' : 'available' };
  };

  const renderQuest = (quest, kid) => {
    const record = receipts[quest.id];
    const windowOpen = undoWindowOpen(record, now);
    const left = windowOpen ? Math.ceil((record.deadline - now) / 1000) : 0;
    const canUndo = windowOpen && !!liveActorFor(record.ownerId);
    // A definite note is shown until its own deadline, board read or not. A note with no deadline (an
    // unknown outcome) stays until the next board read replaces it.
    const note = record?.note && !(record.noteUntil <= now) ? record.note : '';
    const status = record && (windowOpen || record.busy || note) && record.status !== 'undone'
      ? (record.status === 'pending' ? 'pending' : 'lit') : quest.status;
    const shown = record && (windowOpen || record.busy || note) ? {
      executionId: record.executionId,
      status: record.status,
      windowOpen: windowOpen || !!note,
      busy: !!record.busy,
      canUndo,
      quest: { title: quest.title },
      note: note || (canUndo
        ? `Undo is available for ${left} more seconds.`
        : windowOpen ? 'Undo needs your code again.' : ''),
    } : null;
    return (
      <div data-part="board-quest">
      <div
        onPointerDownCapture={() => {
          const current = liveActorFor(kid.id);
          if (current && controlsEnabled) reporter.report(current.generation, 'hold-start');
        }}
        role="presentation"
      >
        <LanternQuest
          key={`${gestureVersion}:${actor?.generation ?? "ambient"}`}
          quest={{ title: quest.title }}
          status={status}
          busy={!!busy[quest.id]}
          hint={`${kid.name}’s lantern`}
          tap={false}
          lanternImage={LANTERN_MARK}
          onAuthorize={() => onIntent(quest, kid)}
          record={shown}
          onUndo={() => undo(quest)}
          bloom={null}
          onBloomClose={() => {}}
        />
      </div>
      {renderMove(quest, kid.id)}
      </div>
    );
  };

  const pinOwner = pin?.owner;
  return (
    // #577 review H3: this provider shadows the app's for the whole board, so it needs the sounds
    // too. Without `assets` it plays nothing, whatever the family allowed.
    <FeedbackProvider actor={soundActor} initialPreferences={kioskSound} availability={board ? 'ready' : 'loading'} assets={AUDIO_ASSETS}>
    <KioskChime receipts={receipts} />
    <div data-component="DisplayRoute" data-state={phase}>
      {actor ? (
        <p role="status" style={{ margin: 0, padding: '0.5rem 1rem' }}>
          {actor.firstName ?? 'Someone'} is lighting lanterns · {actorLeft} seconds left{' '}
          <button type="button" onClick={() => clearActor()}>I’m done</button>
        </p>
      ) : null}
      <p role="status" aria-live="polite" data-part="board-notice" style={{ margin: 0, padding: notice ? '0.5rem 1rem' : 0 }}>{notice}</p>
      <div data-part="board-controls" inert={controlsEnabled ? undefined : true}>
        <Suspense fallback={<LanternLoader label="Opening the family board." />}>
        <Display
          showSnapshotNotice={false}
          familyName="Family board"
          loading={phase === 'error' ? false : !board}
          error={phase === 'error' ? 'The board could not reach the server. It will try again when the connection returns.' : null}
          stale={stale || !live}
          lastSync={lastSync ? clockLabel(lastSync) : null}
          liveLabel={lastSync ? `updated ${clockLabel(lastSync)}` : null}
          kids={kids}
          payday={null}
          renderQuest={renderQuest}
          sharedQuests={sharedQuests}
          sharedTitle={SHARED_TITLE}
          showSharedRow={!!drag?.keys.includes(SHARED)}
          renderSharedAction={(quest) => renderMove(quest, null)}
          columnProps={columnProps}
          showSessionStart={false}
          open={!!pin}
          onOpenChange={(open) => { if (!open) cancelPin(); }}
          actors={pinOwner ? [{ id: pinOwner.id, name: pinOwner.name ?? 'Family member', avatarUrl: pinOwner.avatar ?? null, requiresPin: true, pinLength: 6 }] : []}
          actingId={pinOwner ? pinOwner.id : null}
          sessionTitle={pinOwner ? `Hi ${pinOwner.name ?? 'there'}!` : ''}
          sessionSubtitle="Enter your 4- or 6-digit code to light your lantern."
          sessionSeconds={0}
          pinValue={pin?.value ?? ''}
          pinLength={6}
          pinError={pin?.error ?? null}
          onPinChange={(value) => setPin((p) => (p ? { ...p, value, error: value ? null : p.error } : p))}
          onPinComplete={(value) => submitPin(value)}
          onPinSubmit={(value) => submitPin(value)}
          pinCanSubmit={!!pin && !pin.busy && PIN_SHAPE.test(pin.value)}
        />
        </Suspense>
      </div>
    </div>
    </FeedbackProvider>
  );
}
